'use client';

import { useEffect, useState, type JSX } from 'react';
import dynamic from 'next/dynamic';
import { FormattedMessage, defineMessages } from 'react-intl';
import useUser from '@alltrails/context/hooks/useUser';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import { useFormatMessage } from '@alltrails/core/hooks/useFormatMessage';
import { wrapUrlSafe } from '@alltrails/core/utils/wrapUrlSafe';
import TrailStatus from '@alltrails/types/TrailStatus';
import type { TrailId } from '@alltrails/types/TrailId';
import Info from '@alltrails/denali/icons/Info';
import Toast from '@alltrails/denali/components/Toast';

const ReviewFormModal = dynamic(() => import('@alltrails/reviews/components/NewReviewFormModal/NewReviewFormModal'), { ssr: false });
const PhotoUploadModal = dynamic(() => import('@/components/PhotoUploadModal/PhotoUploadModalWrapper'), { ssr: false });

const messages = defineMessages({
  ADD_PHOTOS: { defaultMessage: 'Add photos' },
  ADD_REVIEW: { defaultMessage: 'Add review' },
  EDIT_TRAIL: { defaultMessage: 'Edit trail' }
});

type Props = {
  trailStatus?: TrailStatus;
  photoCount?: number;
  reviewCount?: number;
  trailId: TrailId;
  trailSlug: string;
};

const PendingTrailToast = ({ trailStatus, photoCount, reviewCount, trailId, trailSlug }: Props): JSX.Element => {
  const {
    formattedDefaultMessages: { ADD_PHOTOS, ADD_REVIEW, EDIT_TRAIL }
  } = useFormatMessage(messages);
  const user = useUser();
  const [isOpen, setIsOpen] = useState(false);
  const languageRegionCode = useLanguageRegionCode();
  const [showPhotosUploadModal, setShowPhotosUploadModal] = useState(false);
  const [showReviewFormModal, setShowReviewFormModal] = useState(false);

  useEffect(() => {
    if (trailStatus && trailStatus === TrailStatus.Pending) {
      setIsOpen(true);
    }
  }, [trailStatus]);

  const onActionClick = () => {
    if (!photoCount) {
      setShowPhotosUploadModal(true);
    } else if (!reviewCount) {
      setShowReviewFormModal(true);
    } else {
      window.open(wrapUrlSafe(`/trail/${trailSlug}/edit?ref=trail-suggest-edit`, languageRegionCode));
    }
  };

  let tooltipActionMsg: string | undefined = undefined;
  if (!photoCount) {
    tooltipActionMsg = ADD_PHOTOS;
  } else if (!reviewCount) {
    tooltipActionMsg = ADD_REVIEW;
  } else {
    tooltipActionMsg = EDIT_TRAIL;
  }

  return (
    <>
      <Toast
        action={onActionClick}
        actionText={tooltipActionMsg}
        icon={{ Component: Info }}
        isOpen={isOpen}
        message={<FormattedMessage defaultMessage="This trail isn’t public yet" />}
        position="alternative"
        testId="pending-trail-toast"
        type="neutral"
      />
      {showReviewFormModal && (
        <ReviewFormModal closeReviewModal={() => setShowReviewFormModal(false)} onReviewAdd={() => setIsOpen(false)} trailId={trailId} user={user!} />
      )}
      {showPhotosUploadModal && (
        <PhotoUploadModal closeModal={() => setShowPhotosUploadModal(false)} trailId={trailId} onPhotosAdd={() => setIsOpen(false)} />
      )}
    </>
  );
};

export default PendingTrailToast;
